import { useCallback, useEffect, useRef } from 'react'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import { CheckCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { Asset } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import { Audio } from './audio-component'
import type { AudioPayload } from './_types'
import { useProjectStore } from '@/hooks/use-project-store'
import { useEditorStore } from '@/hooks/use-editor-store'
import { AudioControllerProvider } from './context'

interface AudioLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

interface SearchResultItem {
  id: string | number
  payload: AudioPayload
  score: number
}

interface SearchResult {
  items: SearchResultItem[]
  next_page_offset?: string
}

const PAGE_SIZE = 10

export const AudioLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: AudioLibraryProps) => {
  const t = useTranslations('audio-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { setIsToolboxOpen } = useProjectStore()
  const { getIsEditorOn, getIsTargetAudio, setTargetSrc } = useEditorStore()
  const isEditorOn = getIsEditorOn()
  const isAudioSelectedInEditor = isEditorOn && getIsTargetAudio()

  const getKey = (
    pageIndex: number,
    previousPageData: SearchResult | null,
    query: string,
  ) => {
    if (previousPageData && !previousPageData.next_page_offset) return null
    return [
      'vector-search',
      pageIndex,
      previousPageData?.next_page_offset,
      query || '__default__',
    ]
  }

  const fetcherImplement = async ([_, pageIndex, next_page_offset, query]: [
    string,
    number,
    string | undefined,
    string,
  ]): Promise<SearchResult> => {
    const finalQuery = query === '__default__' ? '' : query
    const res = await fetcher('/api/media/audio', {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        query: finalQuery,
        limit: PAGE_SIZE,
        offset: next_page_offset,
      }),
    })
    return res.data
  }

  const { data, size, setSize, isLoading } = useSWRInfinite<SearchResult>(
    (index, prev) => getKey(index, prev, query),
    fetcherImplement,
    {
      revalidateFirstPage: false,
    },
  )

  // 列表数据
  const items = data ? data.flatMap((d) => d.items) : []
  const isLoadingMore = isLoading || (size > 0 && !data?.[size - 1])
  const hasMore = data?.[data.length - 1]?.next_page_offset
  const isEmpty = data?.[0]?.items.length === 0
  const isReachingEnd = isEmpty || (data && !hasMore)

  const handleAudioSelect = useCallback(
    (item: SearchResultItem, e: React.MouseEvent) => {
      if (isEditorOn) return
      const { id, payload, score } = item

      // 点击 play 按钮，不走选择
      if ((e.target as HTMLElement).closest('.play')) return

      const assetData: Asset = {
        source: 'elements',
        type: 'audio',
        link: payload.url,
        thumbnail: '',
      }

      if (isAudioSelectedInEditor) {
        setIsToolboxOpen(false)
        setTargetSrc(payload.url)
      } else {
        onSelect(e, assetData)
      }
    },
    [
      isAudioSelectedInEditor,
      isEditorOn,
      setIsToolboxOpen,
      setTargetSrc,
      onSelect,
    ],
  )

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_audios')}</p>
      </div>
    )
  }

  return (
    <AudioControllerProvider>
      {items.map((item) => (
        <motion.div
          key={item.id}
          className={cn(
            'flex-none relative h-[80px]',
            isExpanded ? 'w-full' : 'w-[200px]',
            isEditorOn ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
          onClick={(e) => handleAudioSelect(item, e)}
        >
          <div className="absolute inset-0 rounded-lg overflow-hidden group">
            <Audio item={item.payload} disabled={isEditorOn} />
          </div>
          {selectedAssets.some((asset) => asset.link === item.payload.url) && (
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="bg-gray-900 opacity-[0.1] w-full h-full" />
              <div className="absolute bottom-2 right-2 bg-brand text-white rounded-full p-1">
                <CheckCircle className="w-4 h-4" />
              </div>
            </div>
          )}
        </motion.div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </AudioControllerProvider>
  )
}
